import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import PublicationsData from "./data/publicationsData.js";
import RemovedItemsData from "./data/removedItemsData.js";
import IntroductionData from "./data/introductionData.js";
import ManuscriptsData from "./data/manuscriptsData.js";
import AbbreviationsData from "./data/abbreviationsData.js";
import SecondaryBibliography from "./SecondaryBibliography.js";
import TimelinePanels from "./TimelinePanels";

const sectionMappings = {
  introduction: "Introduction",
  publications: "Publications",
  manuscripts: "Manuscripts",
  removed: "Removed Items",
  abbreviations: "Abbreviations & Works Cited",
  secondary: "Secondary Bibliography"
};

const publicationPanels = [
  "1788 - 1798",
  "1799 - 1800",
  "1801 - 1803",
  "1804 - 1805",
  "1806 - 1807",
  "1808 - 1822",
];
const removedItemsPanels = [
  "1787 - 1788",
  "1790 - 1793",
  "1798 - 1801",
  "1803",
  "1804",
  "1805",
  "1806",
  "1807",
  "1808",
];
const Bibliography = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const sectionParam = (searchParams.get("section") || "introduction").toLowerCase();

  const [activeTab, setActiveTab] = useState("primary");

  const selectedSection = sectionMappings[sectionParam] || "Introduction";

  const publicationsRef = useRef(null);
  const removedItemsRef = useRef(null);

  useEffect(() => {
    if (sectionParam === "secondary") {
      setActiveTab("secondary");
    } else {
      setActiveTab("primary");
    }
  }, [sectionParam]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('section', tab === "secondary" ? "secondary" : "introduction"); // default to intro if "primary"
      return params;
    });
  };

  const getPublicationsContent = (panel) => {
    if (!publicationsRef.current) return "";

    const [startYear, endYear] = panel.split(" - ").map(Number);
    const headings = Array.from(publicationsRef.current.querySelectorAll("h4"));

    let matchedHTML = [];

    headings.forEach((heading) => {
      const year = parseInt(heading.textContent.trim(), 10);
      if (year >= startYear && year <= endYear) {
        const wrapper = document.createElement("div");
        wrapper.className = "timeline-year";
        wrapper.appendChild(heading.cloneNode(true));

        let next = heading.nextElementSibling;
        while (next && next.tagName === "P") {
          wrapper.appendChild(next.cloneNode(true));
          next = next.nextElementSibling;
        }

        matchedHTML.push(wrapper.outerHTML);
      }
    });
    if (matchedHTML.length) {
      return `<div class="timeline">${matchedHTML.join("")}</div>`;
    }
    return "";    
  };

  const parseYearOrMonthYear = (text) => {
    const parts = text.trim().split(" ");
    if (parts.length === 1) {
      const year = parseInt(parts[0], 10);
      return new Date(year, 0, 1);
    } else if (parts.length === 2) {
      const [monthName, yearStr] = parts;
      const month = new Date(`${monthName} 1, 2000`).getMonth();
      const year = parseInt(yearStr, 10);
      return new Date(year, month, 1);
    }
    return null;
  };
  
  const getContent = (panel, contentRef) => {
    if (!contentRef.current) return "";
  
    const [start, end] = panel.split(" - ").map(item => item.trim());
    const startDate = parseYearOrMonthYear(start);
    const endDate = end ? parseYearOrMonthYear(end) : startDate;
  
    const headings = Array.from(contentRef.current.querySelectorAll(".cbb_bold"));
    let matchedHTML = [];
  
    headings.forEach((heading) => {
      const headingText = heading.textContent.trim();
      const headingDate = parseYearOrMonthYear(headingText);
  
      if (headingDate && headingDate >= startDate && headingDate <= endDate) {
        const wrapper = document.createElement("div");
        wrapper.className = "timeline-year";
  
        const yearHeading = document.createElement("h4");
        yearHeading.className = "timeline-year-heading";
        yearHeading.textContent = headingText;
        wrapper.appendChild(yearHeading);
  
        let next = heading.nextElementSibling;
        while (next) {
          if (next.tagName === "P") {
            if (next.classList.contains("cbb_bold")) {
              break;
            }
            if (next.classList.contains("hindent")) {
              wrapper.appendChild(next.cloneNode(true));
            }
          }
          next = next.nextElementSibling;
        }
  
        matchedHTML.push(wrapper.outerHTML);
      }
    });
  
    return matchedHTML.length ? `<div class="timeline">${matchedHTML.join("")}</div>` : "";
  };
    const sections = {
    Introduction: (
      <IntroductionData />
    ),
    Publications: (
      <>
        <h3>Publications</h3>
        <h5><em>Last updated 04/04/2024</em></h5>
        <p>"A" at the end of a citation indicates that a work is indisputably written by Brown, while "B" indicates a high degree of confidence of Brown's authorship. Brown's periodical writing also includes a range of hybrid texts in which Brown excerpts, summarizes, and otherwise creatively edits and shapes other works while adding his own implicit or explicit commentary and contextual framing within each particular magazine issue. Such texts are marked "H" to correspond with "hybrid."</p>

        <TimelinePanels panels={publicationPanels} getContent={getPublicationsContent} />
        <div ref={publicationsRef} style={{ display: "none" }}>
          <PublicationsData />
        </div>
      </>
    ),

    Manuscripts: (
      <>
        <h3>Unpublished Manuscripts</h3>
        <h5><em>Last updated 10/20/2020</em></h5>
        <h4>A. Letters</h4>
        <p>CBBSE</p>
        <p>Census of the Letters of Charles Brockden Brown</p>
        <p>This listing identifies all known letters of Charles Brockden Brown. The census is based on the previous work of Holmes-Cavnar (1998) and Bennett (1976), and updates their findings with scholarship up to 2008. The census provides a complete listing of extant letters (in manuscript or earlier transcriptions) and of letters known only by mention in diaries and other sources, or quoted fragmentarily by their recipients.</p>
        <p>Item number; recipient; date or [date] if uncertain; (Bennett number [B], Holmes-Cavnar number[HC]); "opening words"; MS location</p>
        <ManuscriptsData />

      </>
    ),
    "Removed Items": (
      <>
        <h3>Removed Items</h3>
        <h5><em>Last updated 02/24/2025</em></h5>
        <p>Items Removed from Initial Weber bibliography; Reprinted Items Identified as the Work of Other Writers.</p>
        <p>[Date removed. Removed by. Remarks.]</p>
        <TimelinePanels
          panels={removedItemsPanels}
          getContent={(panel) => getContent(panel, removedItemsRef)}
        />
        <div ref={removedItemsRef} style={{ display: "none" }}>
          <RemovedItemsData />

        </div>

      </>

    ),
    "Abbreviations & Works Cited": (
      <>
      <h3>Abbreviations & Works Cited</h3>

      <AbbreviationsData />
      </>
    )
  };

  return (
    <div className="container mt-4">
      <h1 className="page-header">The Archive</h1>
      <ul className="nav nav-tabs" role="tablist">
        <li
          className="tab-item"
          onClick={() => handleTabClick("primary")}
          style={{ cursor: "pointer", userSelect: "none" }}
        >
          <button className={`nav-link ${activeTab === "primary" ? "active" : ""}`}>
            <i
              className="fas fa-book"
              style={{ color: activeTab === "primary" ? "#bb4816" : "#000000" }}
            ></i>{" "}
            Comprehensive Primary Bibliography
          </button>
        </li>
        <li
          className="tab-item"
          onClick={() => handleTabClick("secondary")}
          style={{ cursor: "pointer", userSelect: "none" }}
        >
          <button className={`nav-link ${activeTab === "secondary" ? "active" : ""}`}>
            <i
              className="fas fa-book"
              style={{ color: activeTab === "secondary" ? "#bb4816" : "#000000" }}
            ></i>{" "}
            Secondary Bibliography
          </button>
        </li>
      </ul>

      <div className="tab-content mt-3">
        {activeTab === "primary" && (
          <div className="tab-pane fade active show">
            <h2>Comprehensive Primary Bibliography</h2>
            <h3>The Writings of Charles Brockden Brown, 1783-1822</h3>
            {Object.entries(sectionMappings)
              .filter(([key]) => key !== "secondary")
              .map(([key, label], index) => (
                <button
                  key={index}
                  className={`btn btn-primary m-1 ${sectionParam === key ? "active" : ""}`}
                  onClick={() => setSearchParams({ section: key })}
                >
                  {label}
                </button>
              ))}
            <div className="mt-4">{selectedSection && sections[selectedSection]}</div>
          </div>
        )}

        {activeTab === "secondary" && (
          <div className="tab-pane fade active show">
            <h2>Secondary Bibliography</h2>
            <h5><em>Last updated 04/04/2024</em></h5>
            <p>This bibliography dates back to 1796. It is continuously updated to reflect the kinds of interdisciplinary work that scholars are pursuing, and includes only those dissertations where Brown's writing is a central focus. The bibliography currently contains 1,265 entries. If a publication is not listed in the bibliography, please   contact <a href="mailto:mark.kamrath@ucf.edu">Mark L. Kamrath</a> or <a href="mailto:philipb@ku.edu">Philip Barnard</a> about items to include.</p>
            <SecondaryBibliography />
          </div>
        )}
      </div>
    </div>
  );
};

export default Bibliography;
